import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Plus, Share2, Trash2 } from 'lucide-react';
import {
  type EnablePublicResult,
  type Survey,
  type SurveyQuestion,
  type SurveyResponse,
  createSurvey,
  enablePublicSurvey,
  listResponses,
  listSurveys,
  respondToSurvey,
} from '@/api/surveys';
import { useDirectory } from '@/hooks/useDirectory';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';

const AUDIENCES = ['all_users', 'managers', 'reps'];

function isAdmin(role: string | undefined): boolean {
  return role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN';
}

export function SurveysPage() {
  const { t } = useTranslation();
  const admin = isAdmin(useAuthStore((s) => s.user?.role));

  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [respondTo, setRespondTo] = useState<Survey | null>(null);
  const [viewResponses, setViewResponses] = useState<Survey | null>(null);
  const [share, setShare] = useState<EnablePublicResult | null>(null);
  const [sharing, setSharing] = useState<string | null>(null);

  async function enableShare(s: Survey) {
    setSharing(s.id);
    try {
      setShare(await enablePublicSurvey(s.id, true));
    } finally {
      setSharing(null);
    }
  }

  const load = useCallback(() => {
    setLoading(true);
    listSurveys()
      .then(setSurveys)
      .catch(() => setSurveys([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            {t('surveys.title')}
          </h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('surveys.subtitle')}</p>
        </div>
        {admin && (
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="survey-new">
            <Plus className="mr-1 h-4 w-4" />
            {t('surveys.new')}
          </Button>
        )}
      </header>

      <div className="overflow-hidden rounded-lg border bg-card">
        {loading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}
        {!loading && surveys.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('surveys.empty')}</p>
        )}
        <ul className="divide-y divide-[var(--color-lop-border)]">
          {surveys.map((s) => (
            <li key={s.id} className="flex items-center gap-3 px-4 py-3" data-testid="survey-row">
              <ClipboardList className="h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--color-lop-text-primary)]">
                  {s.title}
                </p>
                {s.description && (
                  <p className="truncate text-xs text-[var(--color-lop-text-secondary)]">
                    {s.description}
                  </p>
                )}
              </div>
              <span className="rounded-full bg-[var(--color-lop-bg-card-hi)] px-2 py-0.5 text-[10px] text-[var(--color-lop-text-secondary)]">
                {t(`surveys.audience.${s.audience}`, { defaultValue: s.audience })}
              </span>
              <Button size="sm" variant="outline" onClick={() => setRespondTo(s)} data-testid="survey-respond">
                {t('surveys.respond')}
              </Button>
              {admin && (
                <Button size="sm" variant="ghost" onClick={() => setViewResponses(s)}>
                  {t('surveys.viewResponses')}
                </Button>
              )}
              {admin && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={sharing === s.id}
                  onClick={() => enableShare(s)}
                  data-testid="survey-share"
                >
                  <Share2 className="mr-1 h-4 w-4" />
                  {t('surveys.share')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {share && <ShareModal result={share} onClose={() => setShare(null)} />}

      {showNew && (
        <NewSurveyModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      )}
      {respondTo && <RespondModal survey={respondTo} onClose={() => setRespondTo(null)} />}
      {viewResponses && <ResponsesModal survey={viewResponses} onClose={() => setViewResponses(null)} />}
    </div>
  );
}

function NewSurveyModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState('all_users');
  const [questions, setQuestions] = useState<SurveyQuestion[]>([{ id: 'q1', label: '' }]);
  const [submitting, setSubmitting] = useState(false);

  function setQ(i: number, label: string) {
    setQuestions((prev) => prev.map((q, idx) => (idx === i ? { ...q, label } : q)));
  }
  function addQ() {
    setQuestions((prev) => [...prev, { id: `q${prev.length + 1}`, label: '' }]);
  }
  function removeQ(i: number) {
    setQuestions((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function create() {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await createSurvey({
        title: title.trim(),
        description: description.trim() || undefined,
        audience,
        questions: questions.filter((q) => q.label.trim()).map((q) => ({ ...q, label: q.label.trim() })),
      });
      onCreated();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('surveys.newModal.title')}</h3>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('surveys.newModal.titleField')}
        </label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mb-3" data-testid="survey-title" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('surveys.newModal.description')}
        </label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mb-3" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('surveys.newModal.audience')}
        </label>
        <Select value={audience} onChange={(e) => setAudience(e.target.value)} className="mb-4">
          {AUDIENCES.map((a) => (
            <option key={a} value={a}>
              {t(`surveys.audience.${a}`)}
            </option>
          ))}
        </Select>
        <p className="mb-2 text-xs text-[var(--color-lop-text-secondary)]">{t('surveys.newModal.questions')}</p>
        <div className="mb-3 space-y-2">
          {questions.map((q, i) => (
            <div key={q.id} className="flex items-center gap-2">
              <Input value={q.label} onChange={(e) => setQ(i, e.target.value)} placeholder={t('surveys.newModal.questionPlaceholder')} />
              {questions.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeQ(i)} aria-label="remove">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addQ}>
            <Plus className="mr-1 h-4 w-4" />
            {t('surveys.newModal.addQuestion')}
          </Button>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={create} disabled={submitting || !title.trim()} data-testid="survey-create">
            {t('surveys.newModal.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RespondModal({ survey, onClose }: { survey: Survey; onClose: () => void }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState('5');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await respondToSurvey(survey.id, { rating: Number(rating), comments: comments.trim() });
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-1 text-lg font-semibold">{survey.title}</h3>
        {survey.description && (
          <p className="mb-4 text-sm text-[var(--color-lop-text-secondary)]">{survey.description}</p>
        )}
        {done ? (
          <>
            <p className="mb-4 text-sm text-emerald-400">{t('surveys.respondModal.thanks')}</p>
            <div className="flex justify-end">
              <Button size="sm" onClick={onClose}>
                {t('common.close')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('surveys.respondModal.rating')}
            </label>
            <Select value={rating} onChange={(e) => setRating(e.target.value)} className="mb-3">
              {['5', '4', '3', '2', '1'].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('surveys.respondModal.comments')}
            </label>
            <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={4} className="mb-4" data-testid="survey-comments" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={submit} disabled={submitting} data-testid="survey-submit-response">
                {t('surveys.respondModal.submit')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResponsesModal({ survey, onClose }: { survey: Survey; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const dir = useDirectory();
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listResponses(survey.id)
      .then(setResponses)
      .catch(() => setResponses([]))
      .finally(() => setLoading(false));
  }, [survey.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">
          {t('surveys.responsesModal.title', { title: survey.title })}
        </h3>
        {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
        {!loading && responses.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('surveys.responsesModal.empty')}</p>
        )}
        <ul className="space-y-2">
          {responses.map((r) => (
            <li key={r.id} className="rounded-md border border-[var(--color-lop-border)] p-3">
              <p className="text-xs font-medium text-[var(--color-lop-text-primary)]">
                {dir.nameFor(r.respondentUserId)} · {new Date(r.submittedAt).toLocaleString(i18n.language)}
              </p>
              <pre className="mt-1 whitespace-pre-wrap break-words text-xs text-[var(--color-lop-text-secondary)]">
                {JSON.stringify(r.answers, null, 2)}
              </pre>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ShareModal({ result, onClose }: { result: EnablePublicResult; onClose: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/s/${result.token}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the input stays selectable */
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-2 text-lg font-semibold">{t('surveys.shareModal.title')}</h3>
        <p className="mb-3 text-sm text-[var(--color-lop-text-secondary)]">
          {t('surveys.shareModal.subtitle')}
        </p>
        <div className="flex gap-2">
          <Input value={shareUrl} readOnly onFocus={(e) => e.currentTarget.select()} />
          <Button size="sm" onClick={copy} data-testid="survey-share-copy">
            {copied ? t('common.copied') : t('common.copy')}
          </Button>
        </div>
        <div className="mt-4 flex justify-end">
          <Button size="sm" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
